import { http } from '../../http';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import { __ } from '@/js/stores/expressions';

export type AsrChannelType = 'Audio' | 'Text';

export type AsrConfigRule = {
    id: string;
    name: string;
    externalSystemId: string;
    queueIds: string[];
    skillIds: string[];
    channelIds: string[];
    channelTypes: AsrChannelType[];
    isEnabled: boolean;
    stereo: boolean;
    customerChannel: number | null;
    overviewLanguage: string | null;
    anonymize: boolean;
    spellingPhrases: string | null;
};

export type AsrConfigRuleInput = {
    name: string | null;
    externalSystemId: string;
    queueIds: string[];
    skillIds: string[];
    channelIds: string[];
    channelTypes: AsrChannelType[];
    isEnabled: boolean;
    stereo: boolean;
    customerChannel: number | null;
    overviewLanguage: string | null;
    anonymize: boolean;
    spellingPhrases: string | null;
};

export type AsrConfigFilterOption = {
    id: string;
    name: string;
};

export type AsrConfigQueueOption = {
    id: string;
    name: string;
    externalSystemId: string;
};

export type AsrConfigChannelOption = {
    id: string;
    name: string;
    isAudio: boolean;
};

export type AsrConfigTenantFilterOptions = {
    skills: AsrConfigFilterOption[];
    queues: AsrConfigQueueOption[];
    channels: AsrConfigChannelOption[];
};

export type AsrExternalSystemOption = {
    systemId: string;
    systemName: string;
};

export const getAsrConfigRules = async (tenantId: string): Promise<AsrConfigRule[]> => {
    return http
        .backoffice()
        .get<AsrConfigRule[]>(
            `asr-config-rules/${tenantId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-asr-config') } })
        )
        .then((response) => response.data);
};

export const createAsrConfigRule = async (
    tenantId: string,
    data: AsrConfigRuleInput
): Promise<AsrConfigRule> => {
    return http
        .backoffice()
        .post<AsrConfigRule>(
            `asr-config-rules/${tenantId}`,
            data,
            // silent: the mutation's onError shows the backend's actual `detail` message
            // instead of this interceptor's generic per-status-code toast.
            useCustomConfig({ silent: true })
        )
        .then((response) => response.data);
};

export const updateAsrConfigRule = async (
    tenantId: string,
    id: string,
    data: AsrConfigRuleInput
): Promise<AsrConfigRule> => {
    return http
        .backoffice()
        .put<AsrConfigRule>(
            `asr-config-rules/${tenantId}/${id}`,
            data,
            useCustomConfig({ silent: true })
        )
        .then((response) => response.data);
};

export const deleteAsrConfigRule = async (tenantId: string, id: string): Promise<void> => {
    return http
        .backoffice()
        .delete(
            `asr-config-rules/${tenantId}/${id}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-asr-config') } })
        )
        .then(() => undefined);
};

export const getAsrConfigFilterOptions = async (
    tenantId: string
): Promise<AsrConfigTenantFilterOptions> => {
    return http
        .backoffice()
        .get<AsrConfigTenantFilterOptions>(
            `asr-config-rules/filter-options/${tenantId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-asr-config') } })
        )
        .then((response) => response.data);
};

export const getAsrConfigExternalSystems = async (
    tenantId: string
): Promise<AsrExternalSystemOption[]> => {
    return http
        .backoffice()
        .get<AsrExternalSystemOption[]>(
            `asr-config-rules/external-systems/${tenantId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-asr-config') } })
        )
        .then((response) => response.data);
};
